/**
 * Cache de ejes de módulos + tabla de fallback (#87).
 *
 * Vive en un archivo aparte para evitar la dependencia circular entre
 * `accionesPermiso.js` y `modulos.js`. Ambos archivos importan de acá:
 *   - `accionesPermiso.js > ejeDe()` lee de acá como source of truth runtime.
 *   - `modulos.js > sincronizarModulosConFirestore()` usa el fallback
 *     para sembrar el campo `eje` en `/modulos/{codigo}`.
 *   - `modulos.js > cargarEjesEnCache()` llena el cache desde Firestore.
 *   - `modulos.js > setEjeDeModulo()` patchea el cache cuando el admin edita
 *     el eje desde la UI "Items del sidebar".
 *
 * Orden de resolución del eje:
 *   1. cache Firestore (lo que se cargó al login + ediciones de la UI)
 *   2. MODULO_EJE_FALLBACK (esta tabla — lo DECIDIDO)
 *   3. MODULO_EJE_SIN_DECIDIR (la deuda declarada — ver su bloque)
 *   4. 'plataforma' (default último — FAIL-CLOSED, ver `ejeDeFallback`)
 *
 * ⚠️ Cuando agregás un módulo nuevo a MODULOS_CATALOGO, DECLARÁ SU EJE ACÁ.
 * Ya no es "si es plataforma": es siempre, y no depende de que te acuerdes —
 * `ejes.ratchet.test.js` se pone rojo si un módulo del catálogo no está en
 * ninguna de las dos tablas.
 *
 * ⚠️ ESTE MÓDULO NO IMPORTA NADA, Y NO ES ESTILO. Existe para romper el ciclo
 * `accionesPermiso.js` ↔ `modulos.js`, y las dos puntas lo importan. Un
 * `import { EJES } from './accionesPermiso'` acá —que es lo natural, para no
 * repetir los literales— **cierra el ciclo y la app no bootea**: medido, los
 * dos órdenes de entrada tiran `ReferenceError: Cannot access '…' before
 * initialization` (el literal congelado se evalúa en module init, y
 * `accionesPermiso.js:24-31` hace `MODULOS_CATALOGO.map()` en top level).
 * Por eso los ejes van como literales acá y la equivalencia con `EJES` se
 * verifica DESDE AFUERA, en el ratchet.
 */

// ── Defaults de eje por módulo (fallback en código) ──────────────────
export const MODULO_EJE_FALLBACK = Object.freeze({
  // ── Operativo (datos del negocio) ──
  empresas:           'operativo',
  'usuarios-empresa': 'operativo',
  mensajes:           'operativo',
  difusiones:         'operativo',
  accionables:        'operativo',
  casos:              'operativo',
  asignaciones:       'operativo',
  señales:            'operativo',
  calendario:         'operativo',
  metricas:           'operativo',
  slas:               'operativo',
  notificaciones:     'operativo',
  dashboards:         'operativo',
  reportes:           'operativo',
  reuniones:          'operativo',
  warnings:           'operativo',
  biblioteca:         'operativo',
  protocolos:         'operativo',
  mercadolibre:       'operativo',
  integraciones:      'operativo',
  'tienda-nube':      'operativo',
  productos:          'operativo',
  rentabilidad:       'operativo',
  diagnostico:        'operativo',
  consultoria:        'operativo',
  postventa:          'operativo',
  // ── Plataforma (gestión del sistema) ──
  usuarios:               'plataforma',
  organigrama:            'plataforma',
  roles:                  'plataforma',
  'auditoria-permisos':   'plataforma',
  configuracion:          'plataforma',
  'modulos-plataforma':   'plataforma',
  plataformas:            'plataforma',
  planes:                 'plataforma',
  frameworks:             'plataforma',
  dimensiones:            'plataforma',
  'motor-criticidad':     'plataforma',
  reglas:                 'plataforma',
  'prompts-ia':           'plataforma',
  'ia-config':            'plataforma',
  'auto-meeting':         'plataforma',
  'api-terceros':         'plataforma',
  agentes:                'plataforma',
  observabilidad:         'plataforma',
  'audit-logs':           'plataforma',
  // Instalaciones self-service (21-sep-2026): panel INTERNO de Milbrands sobre
  // tiendas que en su mayoría todavía no son clientes. `plataforma` no es sólo
  // por dónde vive la pantalla: `operativo` sembraría el `ver` en los roles de
  // cliente, y un cliente no puede ver qué otras tiendas intentaron instalar la
  // app — es la clase del report LVyJbRGvZebhNU2MmO4G. Se tilda a mano en
  // /permisos (y el seed lo da al rol Operaciones).
  instalaciones:          'plataforma',
  // Eventos (2-sep-2026, T1): `plataforma` A PROPÓSITO aunque el módulo sea
  // producto para clientes — `operativo` sembraría las 7 acciones (incluida
  // `aprobar-reembolsos`, que es PLATA) en los diez roles de cliente en
  // silencio: la clase exacta del report LVyJbRGvZebhNU2MmO4G. El `ver` de los
  // miembros se tilda a mano en /permisos (el ADR rector ya manda "sin
  // defaults"). Cuando el Club se abra a operadores clientes, este eje se
  // revisa con su propia compuerta — igual que sus gemelos `formacion`/`club`,
  // que tienen la misma pregunta abierta acá abajo.
  eventos:                'plataforma',
  // 6-sep-2026: el split Asistir/Organizar mueve las 6 acciones de operador a
  // `eventos-organizar`. Mismo eje y misma razón: `operativo` las sembraría
  // (incluida la de PLATA) en los roles de cliente en silencio.
  'eventos-organizar':    'plataforma',
  // ── Decididos con la lista del 28-sep-2026 (report LVyJbRGvZebhNU2MmO4G) ──
  // Mariano: «el cliente ve sólo reportes, mensajes, formación, club, equipo,
  // proyectos y pulso; todo lo demás es sólo staff». Salieron de
  // `MODULO_EJE_SIN_DECIDIR` el 24-oct-2026 (reacomodo del 23-oct).
  formacion:              'operativo',
  club:                   'operativo',
  roadmap:                'plataforma',
  'ia-aprendizaje':       'plataforma',
  tutoriales:             'plataforma',
  milbrandsOperaciones:   'plataforma',
  // ── Decididos por Mariano el 24-oct-2026 (mismo report) ──
  // Con `debugging` en plataforma el cliente deja de recibir `debugging.crear`
  // (el botón de reportar bug); se lo planteó licencia2 y lo decidió así.
  debugging:              'plataforma',
  // ── Decisión de Mariano del 31-oct-2026 (mismo report), que reemplaza la
  // del 24-oct para proyectos y conocimiento ──
  // «Plataforma: roadmap, debugging, tutoriales, ia-aprendizaje,
  // milbrandsOperaciones, conocimiento. Operativo: proyectos, mercado.»
  // Se le planteó antes que `proyectos` operativo siembra sus acciones en los
  // roles de cliente y deshace la poda del 27-oct; lo decidió así.
  // `proyectos`: lo que un cliente ve lo acota `firestore.rules`
  // (`match /proyectos`: participa o compartido de su empresa), no el eje.
  // `conocimiento`: agregados cross-cartera; sus puertas ya pedían staff
  // (`saludCatalogo.js`, `conocimientoEcosistema.js`), ahora también el eje.
  // `mercado` (26-oct-2026): sus callables de análisis, posición, combos y el
  // portal de estudios exigen el plan a quien no es staff
  // (`exigirModuloContratadoSalvoStaff`), y los rollups de posición y combos
  // que el cliente lee directo, también (firestore.rules, `clienteLeeRollup`).
  proyectos:              'operativo',
  mercado:                'operativo',
  conocimiento:           'plataforma',
})

// ── Sólo staff, y el staff los conserva enteros ─────────────────────────
//
// Los módulos que la lista del 28-sep y las decisiones del 24 y el 31-oct
// pasaron de `operativo` (efectivo) a `plataforma`. «Sólo staff» es SACÁRSELOS al cliente, no al staff: hasta
// ahora los roles de staff los recibían enteros por la base operativa, y
// `seed-roles-v2.js` se los sigue dando enteros por esta lista. Un módulo de
// `plataforma` que no está acá no se amplía a nadie (la regla general del seed:
// el eje plataforma queda acotado por rol).
//
// Vive acá y no en el seed para que la decisión del eje y la de quién la
// conserva sean UNA entrada: `ejes.ratchet.test.js` exige que todo lo de esta
// lista sea `plataforma` en `MODULO_EJE_FALLBACK`.
export const MODULOS_SOLO_STAFF = Object.freeze([
  'roadmap', 'ia-aprendizaje', 'tutoriales', 'milbrandsOperaciones', 'debugging', 'conocimiento',
])

// ── LA DEUDA, DECLARADA — módulos del catálogo SIN eje decidido ──────────
//
// Report LVyJbRGvZebhNU2MmO4G. Diez módulos NO tenían entrada en ninguna
// tabla, así que caían al default `'operativo'` y `seed-roles-v2.js` les
// sembraba **46 pares `(modulo, accion)` en los diez roles no-Wizard** — 460
// tuplas de permiso, en cuentas de cliente incluidas. Ocho se decidieron con la
// lista del 28-sep y los dos últimos (proyectos y debugging) Mariano el
// 24-oct-2026 (arriba): la lista quedó VACÍA y el ratchet la mantiene así.
//
// ⚠️ ESTA LISTA NO ES UNA LISTA DE PERDONES, y por eso está SEPARADA de la
// tabla de arriba en vez de mezclada con ella. Escribir `proyectos: 'operativo'`
// al lado de `empresas: 'operativo'` los vuelve indistinguibles: en seis meses
// nadie sabe cuál se decidió y cuál se heredó del accidente, y el ratchet
// quedaría VERDE sobre exactamente la deuda que motivó el arreglo. Es el molde
// de `scripts/agentes-jurisdiccion-baseline.json` — *"la lista de lo que hoy no
// se cumple, con su motivo y su disparador"*.
//
// ⚠️ SOLO PUEDE ACHICARSE. Cuando Mariano decida uno, se mueve a
// `MODULO_EJE_FALLBACK` con su eje y se BORRA de acá. Un módulo NUEVO no puede
// entrar: el ratchet exige que la lista no crezca.
//
// `ejeHoy` es el eje EFECTIVO de hoy, no una decisión: se escribe para que el
// arreglo del default sea byte-idéntico y no mueva un permiso. La decisión es
// `pregunta`, y es de Mariano — mover un módulo a `'plataforma'` le SACA el
// permiso a los roles de cliente, y si alguno es client-facing eso es una
// regresión funcional que no se puede verificar desde el código.
export const MODULO_EJE_SIN_DECIDIR = Object.freeze({})

/**
 * Resolución barata del eje sin tocar Firestore:
 * cache → decidido → sin-decidir → 'plataforma'.
 *
 * ⚠️ EL DEFAULT DE ÚLTIMO RECURSO ES `'plataforma'`, Y ESO ES EL FIX
 * (report LVyJbRGvZebhNU2MmO4G). Era `'operativo'`, o sea el lado que ABRE
 * permisos: `seed-roles-v2.js` deriva `PERMISOS_OPERATIVOS_COMPLETOS` de
 * TODOS los módulos del eje operativo con todas sus funciones (salvo las que
 * `naceApagada`), y esa base la
 * heredan los diez roles no-Wizard — OPERADOR, OWNER_EMPRESA, CONTADOR,
 * AGENCIA_PUBLICIDAD y COLABORADOR_SOPORTE incluidos, que son roles de CLIENTE.
 * Y `requirePermiso` no chequea staff. O sea que un módulo interno que nadie
 * declaraba nacía con todas sus acciones sembradas en cuentas de tenant, en
 * silencio y sin que ningún gate lo frenara.
 *
 * Con `'plataforma'`, el próximo olvido cae del lado que CIERRA: el módulo
 * queda staff-only hasta que alguien lo declare. Fail-closed.
 *
 * ⚠️ No puede TIRAR en vez de devolver un default: esto corre en runtime del
 * front (`accionesPermiso.js:116` → `modulosPorEje` `:127` →
 * `PermisosMatrizPage.jsx:95`), y una excepción acá rompe la UI de permisos.
 * El default seguro ES la forma de fail-closed que corresponde acá.
 *
 * Hoy la rama del default es INALCANZABLE desde el catálogo —las dos tablas lo
 * cubren entero, y el ratchet lo exige—, así que este cambio no mueve un solo
 * eje de los que existen. Lo que cambia es hacia dónde cae el módulo #55.
 */
export function ejeDeFallback(moduloCodigo) {
  return _ejesCache.get(moduloCodigo)
    || MODULO_EJE_FALLBACK[moduloCodigo]
    || MODULO_EJE_SIN_DECIDIR[moduloCodigo]?.ejeHoy
    || 'plataforma'
}

// ── Cache runtime de ejes leídos de Firestore ────────────────────────
const _ejesCache = new Map()

/** Llena el cache desde un array de docs de Firestore. */
export function setEjesCache(modulosDocs) {
  _ejesCache.clear()
  for (const m of modulosDocs || []) {
    if (m && m.codigo && m.eje) _ejesCache.set(m.codigo, m.eje)
  }
}

/** Patch puntual (cuando se guarda un cambio individual desde la UI). */
export function setEjeCache(moduloCodigo, eje) {
  if (eje) _ejesCache.set(moduloCodigo, eje)
}

/** Lectura cruda del cache (sin fallback). `undefined` si no está cargado. */
export function getEjeCache(moduloCodigo) {
  return _ejesCache.get(moduloCodigo)
}

/** Diagnóstico — cuántas entradas hay en el cache. */
export function tamanoEjesCache() {
  return _ejesCache.size
}
